import { useEffect, useState } from "react";
import { getHub, getUserId } from "../../../../core/Global";
import { FontAwesomeIcons } from "../../../../core/Types";

// ---- Customize Board: each person's own settings for the jobs board ---------------------------
// One object per person, one section per view (only the list view has settings so far). New
// settings go into their view's section; anything missing from a saved copy falls back to the
// default, so older saved copies keep working as settings are added.


// ---- List view columns ----

export type ListColumnId =
    "contact" | "type" | "source" | "address" | "metro" | "tech" | "appointment" | "localTime" | "modified"
    | "created" | "statusChange"

/** One piece of what a column shows (Contact: name, phone, company...). A locked part is always
 *  shown: the column is that part. */
export interface ListColumnPart {
    id: string
    title: string
    defaultOn: boolean
    locked?: boolean
}

export interface ListColumnDef {
    id: ListColumnId
    title: string
    /** What the column shows, for the Customize Board list. */
    description: string
    icon: FontAwesomeIcons
    /** The column's share of the row (a weight: the visible columns split the row's width in
     *  these proportions) and the narrowest it may get, in px. */
    pct: number
    min: number
    /** The header's sort key (JobsDashboardNew.jobsSort), when the column can be sorted. */
    sortKey?: string
    /** Shown on a fresh board. */
    defaultOn: boolean
    /** The pieces the column is made of, each switchable on its own (unless locked). */
    parts?: ListColumnPart[]
}

/** Every column the list view can show, in the default order. The job id + status column is
 *  not here: it always comes first (it carries the selection box and the status). */
export const LIST_COLUMNS: ListColumnDef[] = [
    {
        id: "contact", title: "Contact", description: "Customer name, phones, company and email", icon: "address-card", pct: 10, min: 150, sortKey: "name", defaultOn: true,
        parts: [
            { id: "name", title: "Name", defaultOn: true, locked: true },
            { id: "phone", title: "Phone", defaultOn: true },
            { id: "secondaryPhone", title: "Second phone", defaultOn: true },
            { id: "company", title: "Company", defaultOn: true },
            { id: "email", title: "Email", defaultOn: false },
        ]
    },
    {
        id: "type", title: "Job Type", description: "Job type, its group and the job details", icon: "wrench", pct: 10, min: 150, sortKey: "type", defaultOn: true,
        parts: [
            { id: "type", title: "Job type", defaultOn: true, locked: true },
            { id: "group", title: "Job type group", defaultOn: false },
            { id: "details", title: "Job details", defaultOn: true },
        ]
    },
    {
        id: "source", title: "Job Source", description: "Source and its type", icon: "bullhorn", pct: 10, min: 150, sortKey: "source", defaultOn: true,
        parts: [
            { id: "source", title: "Source", defaultOn: true, locked: true },
            { id: "sourceType", title: "Source type", defaultOn: true },
        ]
    },
    {
        id: "address", title: "Address", description: "The job's address", icon: "location-dot", pct: 15, min: 160, sortKey: "address", defaultOn: true,
        parts: [
            { id: "address", title: "Street and city", defaultOn: true, locked: true },
            { id: "zip", title: "Zip code", defaultOn: true },
            { id: "country", title: "Country", defaultOn: true },
        ]
    },
    { id: "metro", title: "Metro", description: "The job's metro", icon: "map", pct: 7, min: 80, sortKey: "metro", defaultOn: true },
    { id: "tech", title: "Technician", description: "Technician and their response", icon: "user-helmet-safety", pct: 10, min: 130, sortKey: "tech", defaultOn: true },
    {
        id: "appointment", title: "Appointment", description: "Appointment date and hours", icon: "calendar-check", pct: 10, min: 130, sortKey: "appointment", defaultOn: true,
        parts: [
            { id: "date", title: "Date and hours", defaultOn: true, locked: true },
            { id: "localTime", title: "Customer's local time", defaultOn: false },
        ]
    },
    { id: "localTime", title: "Local Time", description: "The customer's current time", icon: "clock", pct: 6, min: 90, sortKey: "localTime", defaultOn: true },
    {
        id: "modified", title: "Last Modified", description: "Last change and who made it", icon: "pen-to-square", pct: 10, min: 150, sortKey: "modified", defaultOn: true,
        parts: [
            { id: "date", title: "Date", defaultOn: true, locked: true },
            { id: "by", title: "Who made it", defaultOn: true },
        ]
    },
    {
        id: "created", title: "Created", description: "When the job was created and by whom", icon: "calendar-plus", pct: 10, min: 150, sortKey: "created", defaultOn: false,
        parts: [
            { id: "date", title: "Date", defaultOn: true, locked: true },
            { id: "by", title: "Who created it", defaultOn: true },
        ]
    },
    {
        id: "statusChange", title: "Status Changed", description: "When the status last changed, its note and who changed it", icon: "clock-rotate-left", pct: 12, min: 150, sortKey: "statusChange", defaultOn: false,
        parts: [
            { id: "date", title: "Date", defaultOn: true, locked: true },
            { id: "note", title: "Status note", defaultOn: true },
            { id: "by", title: "Who changed it", defaultOn: true },
        ]
    },
]

/** Space between two columns (part of each column's width, on its right). */
export const LIST_COLUMN_GAP = 12

/** The fixed first column (job id + status). */
export const LIST_ID_COLUMN = { title: "Job ID & Status", pct: 7, min: 100 }

export interface ListColumnSetting {
    id: ListColumnId
    visible: boolean
    /** Parts switched away from their default (missing = the part's default). */
    parts?: Record<string, boolean>
}

/** How a coloured job's row is tinted: "soft" a faint short fade (the default), "fade" the fade
 *  to about the middle of the row (the board before this setting), "full" the light tint over the
 *  whole row. */
export type RowTint = "soft" | "fade" | "full"
export const ROW_TINTS: RowTint[] = ["soft", "fade", "full"]

/** How tightly the board packs its jobs: "default" is the board before this setting; "compact"
 *  puts a list row on one line and tightens the cards; "comfortable" gives both more room. */
export type BoardDensity = "compact" | "default" | "comfortable"
export const BOARD_DENSITIES: BoardDensity[] = ["compact", "default", "comfortable"]

// ---- Grid view card ----
// A card shows what the calendar's cards show: status and job id, the customer and their phone,
// the address, the technician and the appointment. Each of those but the customer can
// be switched off, and a few more details switched on.

export type GridFieldId = "phone" | "address" | "tech" | "appointment" | "jobType" | "source" | "localTime" | "tags"

export interface GridFieldDef {
    id: GridFieldId
    title: string
    description: string
    icon: FontAwesomeIcons
    defaultOn: boolean
}

export const GRID_FIELDS: GridFieldDef[] = [
    { id: "phone", title: "Phone", description: "The customer's phone, next to their name", icon: "phone-rotary", defaultOn: true },
    { id: "address", title: "Address", description: "The job's address, on one line", icon: "location-dot", defaultOn: true },
    { id: "tech", title: "Technician", description: "Technician's name and their response", icon: "user-helmet-safety", defaultOn: true },
    { id: "jobType", title: "Job Type", description: "The job type", icon: "wrench", defaultOn: false },
    { id: "source", title: "Job Source", description: "Where the job came from", icon: "bullhorn", defaultOn: false },
    { id: "appointment", title: "Appointment", description: "The appointment's date and hours", icon: "calendar-check", defaultOn: true },
    { id: "localTime", title: "Local Time", description: "The customer's current time", icon: "earth-americas", defaultOn: false },
    { id: "tags", title: "Tags & Flags", description: "The job's tags and flags", icon: "tags", defaultOn: false },
]

export interface JobsBoardSettings {
    /** How coloured jobs are tinted: one choice for every view (list, grid, and any added later). */
    rowTint: RowTint
    /** How tightly jobs are packed: one choice for every view, like rowTint. */
    density: BoardDensity
    list: {
        /** Every known column, in the person's order. */
        columns: ListColumnSetting[]
    }
    grid: {
        /** Fields switched away from their default (missing = the field's default). */
        fields: Partial<Record<GridFieldId, boolean>>
    }
}

/** Whether a grid card shows an optional field. */
export const gridFieldOn = (settings: JobsBoardSettings, id: GridFieldId) =>
    settings.grid.fields[id] ?? GRID_FIELDS.find(f => f.id == id)?.defaultOn ?? false

const defaultListColumns = (): ListColumnSetting[] => LIST_COLUMNS.map(c => ({ id: c.id, visible: c.defaultOn }))

export const defaultBoardSettings = (): JobsBoardSettings => ({ rowTint: "soft", density: "default", list: { columns: defaultListColumns() }, grid: { fields: {} } })

/** A saved copy made whole: unknown columns dropped, columns added since then appended. */
const normalize = (saved?: Partial<JobsBoardSettings>): JobsBoardSettings => {
    const savedColumns = saved?.list?.columns ?? []
    const known = savedColumns.filter(c => LIST_COLUMNS.some(d => d.id == c.id))
    const missing = LIST_COLUMNS.filter(d => !known.some(c => c.id == d.id)).map(d => ({ id: d.id, visible: d.defaultOn }))
    const columns: ListColumnSetting[] = [...known, ...missing]

    // Email was a column of its own before it became a part of Contact: a person who had it on
    // keeps seeing it, now inside Contact.
    if (savedColumns.some(c => (c.id as string) == "email" && c.visible)) {
        const contact = columns.find(c => c.id == "contact")!
        if (contact.parts?.email == undefined) contact.parts = { ...contact.parts, email: true }
    }

    // Row colours were a list setting before they covered every view: an old list choice carries over.
    const savedTint = (saved?.rowTint ?? (saved?.list as any)?.rowTint) as RowTint
    const rowTint = ROW_TINTS.includes(savedTint) ? savedTint : "soft"
    const fields: Partial<Record<GridFieldId, boolean>> = {}
    GRID_FIELDS.forEach(f => {
        const v = (saved?.grid?.fields as any)?.[f.id]
        if (typeof v == "boolean") fields[f.id] = v
    })
    const density = BOARD_DENSITIES.includes(saved?.density as BoardDensity) ? saved!.density! : "default"
    return { rowTint, density, list: { columns }, grid: { fields } }
}

/** Whether a column shows one of its parts (a locked part always does). */
export const columnPartOn = (settings: JobsBoardSettings, column: ListColumnId, part: string) => {
    const def = LIST_COLUMNS.find(c => c.id == column)?.parts?.find(p => p.id == part)
    if (!def) return false
    if (def.locked) return true
    return settings.list.columns.find(c => c.id == column)?.parts?.[part] ?? def.defaultOn
}


// ---- Storage: per person, on the server (accounts/profile/boardsettings) ----
// The server copy follows the person to every device. This browser keeps a copy too, so the
// board draws with the person's columns at once and the server answer only corrects it.

const ROUTE = "/accounts/profile/boardsettings"
const storageKey = () => "rinvox-jobs-board-settings-" + (getUserId() ?? "anon")

let cache: { key: string, value: JobsBoardSettings } | undefined
// The person whose server copy was asked for (once per person per page load).
let fetchedFor: string | undefined
let saveTimer: ReturnType<typeof setTimeout> | undefined
const listeners = new Set<() => void>()

const readLocal = (key: string): Partial<JobsBoardSettings> | undefined => {
    try { return JSON.parse(localStorage.getItem(key) ?? "null") ?? undefined } catch { return undefined }
}

const setCache = (key: string, value: JobsBoardSettings) => {
    cache = { key, value }
    try { localStorage.setItem(key, JSON.stringify(value)) } catch { }
    listeners.forEach(l => l())
}

/** Asks the server for the person's copy. None there yet: this browser's copy (if any) is
 *  uploaded, so settings made before they lived on the server are not lost. */
const fetchFromServer = (key: string) => {
    if (fetchedFor == key) return
    fetchedFor = key
    getHub().Get(ROUTE, res => {
        if (!res.success || storageKey() != key) return
        if (res.data) setCache(key, normalize(res.data))
        else if (readLocal(key)) getHub().Post(ROUTE, getBoardSettings(), () => { })
    }, () => { fetchedFor = undefined })
}

export const getBoardSettings = (): JobsBoardSettings => {
    const key = storageKey()
    if (cache?.key != key) cache = { key, value: normalize(readLocal(key)) }
    fetchFromServer(key)
    return cache.value
}

/** Applies at once; the server write waits for a short pause, so dragging and toggling in a
 *  row sends one request. */
export const saveBoardSettings = (value: JobsBoardSettings) => {
    setCache(storageKey(), value)
    if (saveTimer) clearTimeout(saveTimer)
    saveTimer = setTimeout(() => getHub().Post(ROUTE, value, () => { }), 600)
}

/** The settings, re-rendering the caller whenever they are saved (or the board is resized). */
export const useBoardSettings = () => {
    // A tick, not the value: the board's width (setBoardListWidth) notifies the same listeners
    // without changing the settings object, and its users must re-render too.
    const [, tickSet] = useState<number>(0)
    useEffect(() => {
        const l = () => tickSet(n => n + 1)
        listeners.add(l)
        return () => { listeners.delete(l) }
    }, [])
    return getBoardSettings()
}


// ---- Column layout ----

/** The list view's visible columns, in order, for this window width. */
export const visibleListColumns = (settings: JobsBoardSettings, screenWidth: number): ListColumnDef[] =>
    settings.list.columns
        .filter(c => c.visible)
        .map(c => LIST_COLUMNS.find(d => d.id == c.id)!)
        .filter(d => !!d)


// ---- Smart widths ----
// The visible columns share the row's width by their weights, so they always fill the board
// whatever is switched on. A column never gets narrower than its minimum; when the minimums
// alone don't fit (a small screen, many columns) the row gets wider and the board scrolls
// sideways.

// Space in a row that is not columns: the colour band's lane (14), the selection box (24), the
// row's right padding and room for the vertical scrollbar.
const ROW_CHROME = 14 + 24 + 2 + 16
// The row's action icons (up to six, plus their right margin), on the row's one line when compact.
export const COMPACT_ACTIONS_WIDTH = 210

// The list's width (the board measures it), shared with every row and group header.
let listWidth = 0
export const setBoardListWidth = (w: number) => {
    if (Math.abs(w - listWidth) < 1) return
    listWidth = w
    listeners.forEach(l => l())
}

export interface ListLayout {
    columns: ListColumnDef[]
    /** Width of the fixed job id + status column. */
    idWidth: number
    width: (id: ListColumnId) => number
    /** All columns together (the row's minimum width). */
    total: number
    /** Whether a column shows one of its parts. */
    part: (column: ListColumnId, part: string) => boolean
    /** The whole settings object (the grid card reads its fields from it). */
    settings: JobsBoardSettings
}

export const listLayout = (settings: JobsBoardSettings, screenWidth: number): ListLayout => {
    const columns = visibleListColumns(settings, screenWidth)
    // Before the board has measured itself, fall back to the window width.
    // Compact rows are one line: the action icons share it with the columns, and the job id
    // column holds the id and the status time side by side, so it needs more room.
    const compact = settings.density == "compact"
    const avail = (listWidth > 0 ? listWidth : screenWidth - 150) - ROW_CHROME - (compact ? COMPACT_ACTIONS_WIDTH : 0)
    const idColumn = compact ? { pct: LIST_ID_COLUMN.pct + 3, min: LIST_ID_COLUMN.min + 50 } : LIST_ID_COLUMN
    const all = [{ key: "id", pct: idColumn.pct, min: idColumn.min }, ...columns.map(c => ({ key: c.id as string, pct: c.pct, min: c.min }))]

    // Split by weight; a column that would go under its minimum is pinned there and the rest
    // share what is left, until nothing changes.
    const widths: Record<string, number> = {}
    let free = all
    let space = avail
    for (let pass = 0; pass < all.length; pass++) {
        const weight = free.reduce((n, c) => n + c.pct, 0)
        const pinned = free.filter(c => (space * c.pct) / weight < c.min)
        if (pinned.length == 0) {
            free.forEach(c => widths[c.key] = Math.floor((space * c.pct) / weight))
            break
        }
        pinned.forEach(c => { widths[c.key] = c.min; space -= c.min })
        free = free.filter(c => !pinned.includes(c))
        if (free.length == 0) break
    }
    all.forEach(c => { if (widths[c.key] == undefined) widths[c.key] = c.min })

    return {
        columns,
        idWidth: widths["id"],
        width: id => widths[id] ?? LIST_COLUMNS.find(c => c.id == id)!.min,
        total: all.reduce((n, c) => n + widths[c.key], 0),
        part: (column, part) => columnPartOn(settings, column, part),
        settings,
    }
}

/** The list layout, re-rendering the caller when the settings or the board's width change. */
export const useListLayout = (screenWidth: number) => listLayout(useBoardSettings(), screenWidth)
